import { AsyncPipe, CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { StatusSortModel } from '@core/models/status-sort.model';
import { StatusModel } from '@core/models/status.model';
import * as fromRoot from '@core/redux';
import * as StatusAction from '@core/redux/status/status.actions';
import * as TaskActions from '@core/redux/task/task.actions';
import { select, Store } from '@ngrx/store';
import { map, filter, BehaviorSubject } from 'rxjs';

import { BoardColumnComponent } from '../board-column/board-column.component';

declare let jQuery: any;

@Component({
  selector: 'app-board',
  templateUrl: './board.component.html',
  styleUrls: ['./board.component.less'],
  imports: [CommonModule, AsyncPipe, BoardColumnComponent],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoardComponent implements AfterViewInit, OnDestroy {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly elementRef = inject(ElementRef);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isAdmin = toSignal(this.store.select(fromRoot.getUserCanAdmin), { initialValue: false });

  protected readonly isShowBacklog = toSignal(this.store.select(fromRoot.getShowBackLog), { initialValue: false });

  private readonly isSortableInit = signal<boolean>(false);
  private readonly statusesSubject = new BehaviorSubject<StatusModel[]>([]);
  protected readonly statusList$ = this.statusesSubject.asObservable();

  constructor() {
    // Подписываемся на изменения статусов из store
    this.store.pipe(select(fromRoot.getStatusEntities), takeUntilDestroyed(this.destroyRef)).subscribe(statuses => {
      this.statusesSubject.next([...statuses]);
    });

    // Handle route params for task opening
    this.route.params
      .pipe(
        map(params => params['taskId']),
        filter((taskId): taskId is string => !!taskId),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(taskId => this.store.dispatch(new TaskActions.OpenAction(taskId)));
  }

  ngAfterViewInit(): void {
    if (this.isAdmin()) {
      this.initSortable();
    }
  }

  ngOnDestroy(): void {
    this.destroySortable();
  }

  protected onCreateNewStatus(): void {
    const model = new StatusModel();
    model.name = 'Новый статус';
    this.store.dispatch(new StatusAction.CreateAction(model));
  }

  private initSortable(): void {
    jQuery(this.elementRef.nativeElement).sortable({
      items: '> .board__column:gt(0)',
      handle: '.board-column__dd',
      placeholder: 'column-placeholder',
      dropOnEmpty: true,
      tolerance: 'pointer',
      axis: 'x',
      update: () => {
        const elements = Array.from(
          this.elementRef.nativeElement.getElementsByClassName('board__column')
        ) as HTMLElement[];

        // Получаем текущие статусы
        const currentStatuses = this.statusesSubject.getValue();

        // Создаем новый массив статусов в нужном порядке
        const reorderedStatuses = elements
          .map(element => {
            const statusId = element.getAttribute('statusid');
            return currentStatuses.find(status => status.id === statusId);
          })
          .filter((status): status is StatusModel => status !== undefined);

        // Обновляем порядок через subject для мгновенного UI-обновления
        this.statusesSubject.next(reorderedStatuses);

        // Сохраняем новый порядок в store
        const statusSort = new StatusSortModel();
        statusSort.id = reorderedStatuses.map(status => status.id);

        this.store.dispatch(new StatusAction.SortAction(statusSort));
      },
    });
    this.isSortableInit.set(true);
  }

  private destroySortable(): void {
    if (this.isSortableInit()) {
      this.isSortableInit.set(false);
      jQuery(this.elementRef.nativeElement).sortable('destroy');
    }
  }
}
